import React from 'react';
import { BookOpen, Heart, Shield, ShieldCheck, ArrowRight, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const articles = [
  {
    title: 'Mental Health Resources',
    description: 'Educational materials, crisis hotlines, self-help tools, and community support resources for individuals and families in Maryland.',
    href: '/resources/mental-health',
    icon: BookOpen,
    color: 'blue',
  },
  {
    title: 'Depression Treatment',
    description: 'Understanding depression, recognizing warning signs, and learning about evidence-based treatment options available at SDORA Wellbeing.',
    href: '/resources/depression-treatment',
    icon: Heart,
    color: 'indigo',
  },
  {
    title: 'Anxiety Treatment',
    description: 'Information about anxiety disorders, common symptoms, and how outpatient therapy and medication management can help.',
    href: '/resources/anxiety-treatment',
    icon: Shield,
    color: 'emerald',
  },
  {
    title: 'PTSD Support',
    description: 'Trauma recovery resources, PTSD symptoms, treatment approaches, and specialized support for veterans and adults.',
    href: '/resources/ptsd-support',
    icon: ShieldCheck,
    color: 'slate',
  },
];

const colorMap = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-600', link: 'text-blue-600' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', icon: 'text-indigo-600', link: 'text-indigo-600' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600', link: 'text-emerald-600' },
  slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-700', link: 'text-slate-700' },
};

const ResourcesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Mental Health Resources & Guides"
        description="Free mental health resources, articles, and guides from SDORA Wellbeing. Learn about depression, anxiety, PTSD, and available treatment options in Pikesville and Baltimore County, Maryland."
        path="/resources"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-700 to-indigo-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Mental Health Resources & Guides
          </h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">
            Free educational articles, guides, and tools to help you understand mental health conditions and the treatment options available to you.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Intro */}
        <section className="mb-12" data-testid="resources-intro">
          <p className="text-gray-700 leading-relaxed mb-4">
            At SDORA Wellbeing, we believe that understanding your mental health is the first step toward recovery.
            These resources are designed to provide clear, accurate information about common conditions, available treatments,
            and how to access care in the Pikesville and Baltimore County area.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Whether you are researching for yourself, a family member, or someone you support professionally,
            we hope these guides help you take the next step with confidence.
          </p>
        </section>

        {/* Article Cards */}
        <section className="mb-12" data-testid="resources-articles">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Browse Our Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {articles.map((article) => {
              const colors = colorMap[article.color];
              const Icon = article.icon;
              return (
                <Link
                  key={article.href}
                  to={article.href}
                  className={`${colors.bg} border ${colors.border} rounded-xl p-5 hover:shadow-md transition-shadow group`}
                  data-testid={`resource-card-${article.href.split('/').pop()}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <Icon className={`w-6 h-6 ${colors.icon} flex-shrink-0 mt-0.5`} />
                    <h3 className="font-semibold text-gray-900">{article.title}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{article.description}</p>
                  <span className={`text-sm font-medium ${colors.link} inline-flex items-center gap-1 group-hover:underline`}>
                    Read article <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Self-Assessment */}
        <section className="mb-12 bg-purple-50 rounded-xl p-6 border border-purple-100" data-testid="resources-assessment">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Free Mental Health Screening</h2>
          <p className="text-sm text-gray-700 mb-3">
            Not sure where to start? Our free, confidential self-assessment tools (PHQ-9 for depression, GAD-7 for anxiety, ASRS for ADHD)
            can help you understand your symptoms. Results are not a diagnosis but can guide your next conversation with a provider.
          </p>
          <Link to="/self-assessment" className="text-sm text-purple-700 font-semibold hover:underline inline-flex items-center gap-1">
            Take a free screening <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* Service Areas */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Areas We Serve</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/areas/baltimore-city" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
              <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700">Baltimore City Services</span>
            </Link>
            <Link to="/areas/baltimore-county" className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
              <ArrowRight className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="text-sm font-medium text-gray-700">Baltimore County Services</span>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200" data-testid="resources-cta">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Ready to Take the Next Step?</h2>
          <p className="text-sm text-gray-600 mb-4">
            If you or someone you know needs support, we are here to help. Call us or get started online.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-blue-700 border border-blue-200 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
